// Collabora postMessage plumbing (see .omo/evidence/oauth-app-authentication/a7-wopi-fieldnames.txt).

import type { WopiToken } from 'services/auth/wopi';

// Collabora warns when 15 minutes of token life remain.
export const WARNING_WINDOW_MS = 900_000;
// Must stay below the warning window, otherwise a floor-length token is never fresh.
export const FRESH_MARGIN_MS = 60_000;
// Host-timer fallback: runs the refresh sequence this long before the held token expires.
export const FALLBACK_LEAD_MS = 300_000;
// Remount deadline: last moment to swap in an undelivered fresh token.
export const DEADLINE_LEAD_MS = 60_000;

export const isFresh = (token: WopiToken | null, now: number = Date.now()): boolean => (
  token !== null && token.ttl > now + WARNING_WINDOW_MS + FRESH_MARGIN_MS
);

export type CollaboraMessage = {
  messageId: string;
  values: Record<string, unknown>;
};

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

// COOL posts JSON strings ({ MessageId, SendTime, Values }); an already-parsed object is tolerated.
export const parseCollaboraMessage = (data: unknown): CollaboraMessage | null => {
  let parsed: unknown = data;
  if (typeof data === 'string') {
    try {
      parsed = JSON.parse(data);
    } catch {
      return null;
    }
  }
  if (!isRecord(parsed) || typeof parsed.MessageId !== 'string') return null;
  return { messageId: parsed.MessageId, values: isRecord(parsed.Values) ? parsed.Values : {} };
};

// Inbound events are trusted ONLY from the Collabora origin AND this exact iframe window.
export const isTrustedMessage = (
  event: MessageEvent,
  frame: HTMLIFrameElement | null,
  origin: string | null,
): boolean => origin !== null && event.origin === origin && frame !== null && event.source === frame.contentWindow;

// Posts to the iframe's specific origin (never '*'). Throws when the frame or origin is unavailable.
export const postToCollabora = (
  frame: HTMLIFrameElement | null,
  origin: string | null,
  messageId: string,
  values: Record<string, unknown>,
): void => {
  if (!frame?.contentWindow || origin === null) throw new Error('Collabora frame unavailable');
  frame.contentWindow.postMessage(JSON.stringify({ MessageId: messageId, SendTime: Date.now(), Values: values }), origin);
};
